import {
    useCallback, useEffect, useMemo, useState,
} from 'react';
import { useForm } from 'react-hook-form';
import { number, object, ObjectSchema } from 'yup';
import { useTranslation } from 'react-i18next';

import { STATUS_OK } from 'constants/constants';
import { useCreatePortfolioActions, useCreatePortfolioSelectors } from 'datasource/CreatePortfolio';
import { applyThousandSeparator } from 'ui-library/components/NumberInput/utils';
import { usePortfolioProcessNavigation } from 'hooks/helpers/usePortfolioProcessNavigation';
import { getValidationMessage } from 'utils/validation';
import { IsAllDataNull } from 'validation/validation';
import { useYupValidationResolver } from 'hooks/useYupValidationResolver';
import { TPageError } from 'datasource/PortfolioProcessLayout/PortfolioFlowLayoutManager.types';
import { convertValuesToTwoDecimalPlaces, formatFloatingNumber } from 'utils';
import { PensionSituationFormData } from './usePensionSituation.types';

export const usePensionSituation = () => {
    const { t } = useTranslation();

    const {
        advisoryIdSelector, isJointAccountSelector, currentClientSelector, jointClientSelector, advisoryDataSelector, proxyContactId,
        totalPillar3aAmountSelector: pillar3aAmount = 0, totalVBAmountSelector: vbAmount = 0,
    } = useCreatePortfolioSelectors();

    const { saveAdvisoryDataAction } = useCreatePortfolioActions();

    const [continueButtonLoading, setContinueButtonLoading] = useState<boolean>(false);
    const [pageErrors, setPageErrors] = useState<TPageError | undefined>(undefined);

    const initialPensionSituation = useMemo(() => (advisoryDataSelector?.metadata?.pensionSituation ? convertValuesToTwoDecimalPlaces(advisoryDataSelector?.metadata?.pensionSituation) : null), [advisoryDataSelector?.metadata?.pensionSituation]);

    const [showForm, setShowForm] = useState<boolean>();

    useEffect(() => {
        setShowForm(!!initialPensionSituation && !IsAllDataNull(initialPensionSituation));
    }, [initialPensionSituation]);

    const { numberError } = getValidationMessage();

    const numberInputValidation = number()
        .transform((value: number, originalValue: string) => (originalValue === '' ? null : value))
        .nullable()
        .typeError(numberError);

    const schema: ObjectSchema<PensionSituationFormData> = useMemo(() => object().shape({
        otherVbAmount: numberInputValidation,
        otherPillar3aAmount: numberInputValidation,
        pensionFund: numberInputValidation,
        lifeInsurance3aVb: numberInputValidation,
    }), [t]);

    const resolver = useYupValidationResolver(schema);

    const {
        control, handleSubmit, formState: { errors, isDirty }, watch,
    } = useForm({
        resolver,
        defaultValues: initialPensionSituation,
    });

    const data = watch();

    const total3AInvestments = useMemo(
        () => (pillar3aAmount ? applyThousandSeparator(`${pillar3aAmount.toFixed(2)}`, '’') : null),
        [pillar3aAmount, applyThousandSeparator],
    );

    const totalVBInvestments = useMemo(
        () => (vbAmount
            ? applyThousandSeparator(`${vbAmount.toFixed(2)}`, '’')
            : null),
        [vbAmount],
    );

    const totalPension = useMemo(() => {
        const totalValue = (+pillar3aAmount || 0) + (+vbAmount || 0) + (+data?.otherPillar3aAmount || 0) + (+data?.otherVbAmount || 0) + (+data?.pensionFund || 0) + (+data?.lifeInsurance3aVb || 0);

        return formatFloatingNumber(totalValue);
    }, [pillar3aAmount, vbAmount, data]);

    const showTotalPension = useMemo(() => !!pillar3aAmount || !!vbAmount || !!data?.otherPillar3aAmount || !!data?.otherVbAmount || !!data?.pensionFund || !!data?.lifeInsurance3aVb, [pillar3aAmount, vbAmount, data]);

    const saveData = async (payload: PensionSituationFormData & { pillar3aAmount?: number | null, vbAmount?: number | null } | null) => {
        if (advisoryIdSelector) {
            const result: any = await saveAdvisoryDataAction({
                key: 'pension-situation',
                payload,
                setPageErrors,
            });

            if (result?.status === STATUS_OK) {
                unlockNavigationAction();
                stepComplete();
            }
            setContinueButtonLoading(false);
        }
    };

    const onSubmit = useCallback(async (dta: PensionSituationFormData) => {
        if (showForm && (isDirty || pillar3aAmount || vbAmount)) {
            setContinueButtonLoading(true);

            await saveData({ ...dta, pillar3aAmount: pillar3aAmount || null, vbAmount: vbAmount || null });
        } else if (!showForm && !!initialPensionSituation && !IsAllDataNull(initialPensionSituation)) {
            setContinueButtonLoading(true);
            await saveData({
                vbAmount: null,
                pillar3aAmount: null,
                otherVbAmount: null,
                otherPillar3aAmount: null,
                pensionFund: null,
                LifeInsurance3aVb: null,
            });
        } else {
            unlockNavigationAction();
            stepComplete();
        }
    }, [isDirty, showForm, pillar3aAmount, vbAmount]);

    const { stepComplete, unlockNavigationAction } = usePortfolioProcessNavigation({
        saveOnContinue: useCallback(handleSubmit(onSubmit), [onSubmit]),
        continueInProgress: continueButtonLoading,
        enableAutoStepComplete: !isDirty,
        pageErrors,
    });

    return {
        data: {
            total3AInvestments,
            totalVBInvestments,
            totalPension,
            showTotalPension,
        },
        isLoading: false,
        isJoint: isJointAccountSelector,
        clientName: currentClientSelector?.name || '',
        proxyName: jointClientSelector?.name || '',
        formData: data,
        control,
        errors,
        showForm,
        setShowForm,
    };
};
